import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const MAX_FRAME_DELTA_MS = 1000 / 30;

/**
 * Press logo marquee — continuous scroll without hover pause.
 * Uses requestAnimationFrame so animation keeps running during page scroll.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class PresseMarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /** @type {number | null} */
  #marqueeWidth = null;

  /** @type {number | null} */
  #animationFrame = null;

  /** @type {number} */
  #position = 0;

  /** @type {number | null} */
  #lastTimestamp = null;

  /** @type {number} */
  #pixelsPerSecond = 0;

  /** @type {number} */
  #loopDistance = 0;

  /** @type {number} */
  #lastWindowWidth = window.innerWidth;

  /** @type {boolean} */
  #prefersReducedMotion = false;

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.#prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.#prefersReducedMotion) {
      this.setAttribute('data-disabled', '');
      return;
    }

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#updateLoopDistance();
    this.#setPixelsPerSecond(numberOfCopies);
    this.#startAnimation();

    window.addEventListener('resize', this.#handleResize);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAnimation();
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #startAnimation() {
    this.#stopAnimation();

    const direction = this.#getDirection();
    this.#position = direction > 0 ? -this.#loopDistance : 0;
    this.#lastTimestamp = null;
    this.#animationFrame = requestAnimationFrame(this.#animate);
  }

  #stopAnimation() {
    if (this.#animationFrame !== null) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.#lastTimestamp = null;
    }
  };

  #getDirection() {
    return this.getAttribute('data-movement-direction') === 'reverse' ? 1 : -1;
  }

  #updateLoopDistance() {
    const loopWidth = this.refs.wrapper.scrollWidth / 2;
    const gap = Number.parseFloat(getComputedStyle(this).getPropertyValue('--marquee-gap')) || 0;

    this.#loopDistance = loopWidth > 0 ? loopWidth + gap / 2 : 0;
  }

  /**
   * @param {number} timestamp
   */
  #animate = (timestamp) => {
    if (this.#lastTimestamp === null) {
      this.#lastTimestamp = timestamp;
    }

    const delta = Math.min(timestamp - this.#lastTimestamp, MAX_FRAME_DELTA_MS);
    this.#lastTimestamp = timestamp;

    const direction = this.#getDirection();
    const limit = this.#loopDistance;

    this.#position += direction * this.#pixelsPerSecond * (delta / 1000);

    if (limit > 0) {
      if (direction < 0 && this.#position <= -limit) {
        this.#position += limit;
      } else if (direction > 0 && this.#position >= 0) {
        this.#position -= limit;
      }
    }

    this.refs.wrapper.style.transform = `translate3d(${this.#position}px, 0, 0)`;
    this.#animationFrame = requestAnimationFrame(this.#animate);
  };

  /**
   * @param {number} numberOfCopies
   */
  #setPixelsPerSecond(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const durationSeconds = Math.sqrt(numberOfCopies) * speedFactor;
    const distance = this.#loopDistance || 1;

    this.#pixelsPerSecond = durationSeconds > 0 ? distance / durationSeconds : 0;
    this.style.setProperty('--marquee-speed', `${durationSeconds}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  #handleResize = debounce(async () => {
    const nextWidth = window.innerWidth;
    if (nextWidth === this.#lastWindowWidth) return;

    this.#lastWindowWidth = nextWidth;

    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    if (!isHorizontalResize) return;

    const currentNumberOfCopies = this.refs.content.childElementCount;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#updateLoopDistance();
    this.#setPixelsPerSecond(newNumberOfCopies);

    const direction = this.#getDirection();
    this.#position = direction > 0 ? -this.#loopDistance : 0;
    this.#lastTimestamp = null;
  }, 250);

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    for (const element of clone.querySelectorAll('[ref]')) {
      element.removeAttribute('ref');
    }

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const itemsToRemove = Math.min(numberOfCopies, content.children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get('presse-marquee-component')) {
  customElements.define('presse-marquee-component', PresseMarqueeComponent);
}
